import {get} from 'lodash';
import orderBy from 'lodash/orderBy';
import round from 'lodash/round';
import moment from 'moment';
import React, {
  CSSProperties,
  FunctionComponent,
  useEffect,
  useState,
} from 'react';
import {useDispatch} from 'react-redux';
import swal from 'sweetalert2';
import {getEndpoint, getHeaders} from '../../helpers/endpoint';
import {INewsFeed, INewsFeedStat} from '../../types/newsFeed';
import {OrderType} from '../NotificationsList';
import {newsPostStatus} from '../UpdateNewsPost';

const Styles: Record<string, CSSProperties> = {
  container: {
    margin: 16,
    paddingLeft: 8,
    paddingRight: 8,
    paddingTop: 16,
    paddingBottom: 16,
    width: '100%',
    backgroundColor: 'white',
  },
};

interface NewsFeedListProps {}

export const getNewsFeed = (onSuccess: (response: INewsFeed[]) => void) => {
  return (_dispatch, getState) => {
    return fetch(getEndpoint('getNewsFeed'), {
      method: 'GET',
      headers: getHeaders(getState().authentication),
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
        } else {
          onSuccess(data.newsPosts);
        }
      })
      .catch(error => {});
  };
};

export const getNewsFeedStats = (
  onSuccess: (response: INewsFeedStat) => void,
) => {
  return (_dispatch, getState) => {
    return fetch(getEndpoint('getNewsFeedStats'), {
      method: 'GET',
      headers: getHeaders(getState().authentication),
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
        } else {
          onSuccess(data.stats);
        }
      })
      .catch(error => {});
  };
};

export const deleteNewsPostById = (
  newsPostId: string,
  onSuccess: () => void,
) => {
  return (_dispatch, getState) => {
    return fetch(getEndpoint('deleteNewsPostById', newsPostId), {
      method: 'DELETE',
      headers: getHeaders(getState().authentication),
    })
      .then(response => response.json())
      .then(data => {
        if (data.error) {
        } else {
          // eslint-disable-next-line no-unused-expressions
          onSuccess?.();
        }
      })
      .catch(error => {});
  };
};

const orderNewsFeeds = (
  newsFeeds: INewsFeed[],
  order: OrderType,
  newsFeedStats: INewsFeedStat | undefined,
) => {
  if (order.type === 'name' || order.type === 'status') {
    return orderBy(newsFeeds, [order.type], [order.sort]);
  } else {
    return orderBy(
      newsFeeds,
      [
        news => {
          return news.id && get(newsFeedStats?.[news.id], order.type);
        },
      ],
      [order.sort],
    );
  }
};

export const NewsFeedList: FunctionComponent<NewsFeedListProps> = ({
  history,
}) => {
  const dispatch = useDispatch();
  const [order, setOrder] = useState<OrderType>({
    type: 'status',
    sort: 'asc',
  });
  const [newsFeed, setNewsFeed] = useState<INewsFeed[]>();
  const [newsFeedStats, setNewsFeedStats] = useState<INewsFeedStat>();

  useEffect(() => {
    dispatch(
      getNewsFeed(response =>
        setNewsFeed(orderNewsFeeds(response, order, newsFeedStats)),
      ),
    );
    dispatch(getNewsFeedStats(response => setNewsFeedStats(response)));
  }, []);

  useEffect(() => {
    if (newsFeed) {
      setNewsFeed(orderNewsFeeds(newsFeed, order, newsFeedStats));
    }
  }, [order]);

  const setOrderNewsFeed = (type: string) => {
    setOrder(state => {
      return {
        type,
        sort:
          state.type === type
            ? state.sort === 'asc'
              ? 'desc'
              : 'asc'
            : state.sort,
      };
    });
  };

  return (
    <div style={Styles.container}>
      <table className="table table-responsive table-striped text-center">
        <thead>
          <tr>
            <th
              onClick={() => setOrderNewsFeed('name')}
              style={{cursor: 'pointer'}}>
              Name
            </th>
            <th
              onClick={() => setOrderNewsFeed('all.sent')}
              style={{cursor: 'pointer'}}>
              Sent
            </th>
            <th
              onClick={() => setOrderNewsFeed('all.viewed')}
              style={{cursor: 'pointer'}}>
              Views
            </th>
            <th
              onClick={() => setOrderNewsFeed('all.clicked')}
              style={{cursor: 'pointer'}}>
              Clicks
            </th>
            <th
              onClick={() => setOrderNewsFeed('uniq.viewed')}
              style={{cursor: 'pointer'}}>
              Unique Views
            </th>
            <th>Open rate</th>
            <th
              onClick={() => setOrderNewsFeed('uniq.clicked')}
              style={{cursor: 'pointer'}}>
              Unique Clicks
            </th>
            <th>Reactivity rate</th>
            <th
              onClick={() => setOrderNewsFeed('status')}
              style={{cursor: 'pointer'}}>
              Status
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          {newsFeed?.map(newsPost => {
            const stats = newsPost.id
              ? newsFeedStats?.[newsPost.id]
              : undefined;
            return (
              <tr key={newsPost.id}>
                <th>{newsPost.name}</th>
                <th>{stats?.all.sent || '-'}</th>
                <th>{stats?.all.viewed || '-'}</th>
                <th>{stats?.all.clicked || '-'}</th>
                <th>{stats?.uniq.viewed || '-'}</th>
                <th>
                  {(stats?.all.sent &&
                    stats?.uniq.viewed &&
                    `${round(
                      (stats?.uniq.viewed / stats?.all.sent) * 100,
                      2,
                    )}%`) ||
                    '-'}
                </th>
                <th>{stats?.uniq.clicked || '-'}</th>
                <th>
                  {(stats?.uniq.viewed &&
                    stats?.uniq.clicked &&
                    `${round(
                      (stats?.uniq.clicked / stats?.uniq.viewed) * 100,
                      2,
                    )}%`) ||
                    '-'}
                </th>
                <th>
                  {newsPost.status &&
                    newsPostStatus[
                      newsPost.status === 2 &&
                      newsPost.publishedTimestamp &&
                      moment(newsPost.publishedTimestamp).isSameOrBefore(
                        moment(),
                      )
                        ? 3
                        : newsPost.status
                    ]}
                </th>
                <th>
                  <button
                    className="btn"
                    onClick={() =>
                      history.push(`/news-feed/update/${newsPost.id}`)
                    }>
                    Modifier
                  </button>
                  <button
                    style={{marginLeft: 8, marginRight: 8}}
                    className="btn"
                    onClick={() =>
                      history.push(
                        `/news-feed/create?newsPostId=${newsPost.id}`,
                      )
                    }>
                    <i className="fa fa-clone"></i>
                  </button>
                  <button
                    className="btn btn-danger"
                    onClick={() => {
                      swal({
                        title: 'Are you sure ?',
                        type: 'info',
                        showCloseButton: true,
                        showCancelButton: true,
                        focusConfirm: false,
                        confirmButtonText: 'Yes',
                        cancelButtonText: 'No',
                      }).then(() => {
                        if (newsPost.id) {
                          dispatch(
                            deleteNewsPostById(newsPost.id, () =>
                              dispatch(
                                getNewsFeed(response => setNewsFeed(response)),
                              ),
                            ),
                          );
                        }
                      });
                    }}>
                    <i style={{color: 'white'}} className="fa fa-trash" />
                  </button>
                </th>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};



// WEBPACK FOOTER //
// ./src/pages/NewsFeedList/index.tsx